import React, { type ReactNode, useMemo } from 'react';

import type {
	AnyEntryPoint,
	EntryPointRef,
	RuntimePropsOfEntryPoint,
} from '@atlassian/entry-point-types';
import { InternalEntryPointContainer } from '@atlassian/internal-entry-point-container';
import type { JSResourceReference } from '@atlassian/react-async';

import { ErrorFallbackPreviewPanel } from './ErrorFallbackPreviewPanel';
import { FallbackPreviewPanel } from './FallbackPreviewPanel';

type GlobalObject = {
	icon: ReactNode;
	name: string;
	url: string;
};

type Props<TEntryPoint> = {
	entryPoint: TEntryPoint;
	globalObject: GlobalObject;
	preloadedEntryPointReference: EntryPointRef<TEntryPoint>;
	runtimeProps?: RuntimePropsOfEntryPoint<TEntryPoint>;
};

const emptyEntryPointProps = {} as RuntimePropsOfEntryPoint<AnyEntryPoint>;

export const EntryPointPreviewPanel = <TEntryPoint extends AnyEntryPoint>({
	entryPoint,
	preloadedEntryPointReference,
	runtimeProps = emptyEntryPointProps,
	globalObject,
}: Props<TEntryPoint>): React.JSX.Element => {
	const id = useMemo((): string => {
		return (entryPoint.root as JSResourceReference<any>).getModuleName() || 'unknown';
	}, [entryPoint]);

	return (
		<InternalEntryPointContainer
			id={id}
			entryPointReference={preloadedEntryPointReference}
			fallback={<FallbackPreviewPanel {...globalObject} />}
			errorFallback={() => <ErrorFallbackPreviewPanel {...globalObject} />}
			runtimeProps={runtimeProps}
		/>
	);
};
